// File: src/pages/DashboardPosts.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { fetchPosts } from '../api/posts';
import { deletePost } from '../api/deletePost';
import { deleteUser } from '../api/deleteUser';
import { getUserId, getAuthCode } from '../api/authStorage';
import '../assets/css/DashboardPosts.css';

const DashboardPosts = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, per_page: 12, current_page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalMedia, setModalMedia] = useState(null);

  /** Fetch posts */
  const loadPage = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchPosts(page, pagination.per_page);
      setPosts(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load posts.');
      if (/auth/i.test(err.message)) {
        await logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(pagination.current_page);
    // eslint-disable-next-line
  }, []);

  /** Pagination */
  const handlePage = (newPage) => {
    if (newPage < 1 || newPage > pagination.total_pages) return;
    loadPage(newPage);
  };

  /** Modal handlers */
  const openModal = (media) => setModalMedia(media);
  const closeModal = () => setModalMedia(null);

  /** Delete handler with two options */
  const handleDelete = async (post) => {
    const choice = window.prompt(
      `Post #${post.post_id} by ${post.user.full_name}\n\nType 1 to delete post only\nType 2 to delete user and all their posts`
    );

    if (!choice || !['1','2'].includes(choice)) return;

    try {
      const user_id = getUserId();
      const auth_code = getAuthCode();
      if (!user_id || !auth_code) throw new Error("Admin not authenticated");

      if (choice === '1') {
        // Delete post only
        const reason = prompt("Reason for deleting this post?");
        if (!reason) return;

        await deletePost({ user_id, auth_code, post_id: post.post_id, reason });
        setPosts(prev => prev.filter(p => p.post_id !== post.post_id));
        alert('Post deleted successfully.');
      } else {
        // Delete user
        const ok = window.confirm(`Are you sure you want to delete user @${post.user.username} and all their posts?`);
        if (!ok) return;

        await deleteUser({ target_user_id: post.user.user_id });
        setPosts(prev => prev.filter(p => p.user.user_id !== post.user.user_id));
        alert('User and all their posts deleted successfully.');
      }
    } catch (err) {
      alert(err.message || 'Delete action failed');
    }
  };

  return (
    <div className="dashboard-posts">
      <div className="posts-header">
        <h2>{pagination.total} Posts</h2>
        <p>Click a post to view, or use delete for post/user management.</p>
      </div>

      {loading && <p className="status-msg">Loading posts…</p>}
      {error && <p className="status-msg error">{error}</p>}

      {!loading && !error && (
        <>
          <div className="posts-grid">
            {posts.map(post => (
              <div className="post-card" key={post.post_id}>
                <div className="post-card-header">
                  <div>
                    <h3>Post #{post.post_id}</h3>
                    <p className="posted-by">
                      {post.user.user_id} - <strong>{post.user.full_name}</strong> (@{post.user.username})
                    </p>
                  </div>
                  <button className="delete-btn" onClick={() => handleDelete(post)}>Delete</button>
                </div>

                <div className="post-media-grid">
                  {post.media.map(m => (
                    <div className="media-item" key={m.image_id} onClick={() => openModal(m)}>
                      {m.media_type === 'image' ? <img src={m.image_url} alt="" /> : <video src={m.image_url} muted />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="pagination">
            <button className="pagination-btn" onClick={() => handlePage(pagination.current_page - 1)} disabled={pagination.current_page === 1}>Previous</button>
            <span className="pagination-info">Page {pagination.current_page} of {pagination.total_pages}</span>
            <button className="pagination-btn" onClick={() => handlePage(pagination.current_page + 1)} disabled={pagination.current_page === pagination.total_pages}>Next</button>
          </div>
        </>
      )}

      {modalMedia && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            {modalMedia.media_type === 'image' ? <img src={modalMedia.image_url} alt="" /> : <video src={modalMedia.image_url} controls autoPlay />}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPosts;